import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

/* ---------- types ---------- */
interface MonitorInfo {
  id: number;
  full_name: string;
  email: string;
  role: string;
  current_shift: boolean;
  shift_started_at: string | null;
}

interface PeriodStats {
  shifts: number;
  alerts: number;
  activities: number;
  hours: number;
}

interface RecentShift {
  id: number;
  clock_in: string;
  clock_out: string | null;
  duration_minutes: number | null;
  alerts_count: number;
  activities_count: number;
  handoff_notes: string | null;
}

interface AssignedStore {
  id: number;
  name: string;
  address: string;
}

interface PerformanceData {
  monitor: MonitorInfo;
  today: PeriodStats;
  this_week: PeriodStats;
  last_30_days: PeriodStats;
  recent_shifts: RecentShift[];
  assigned_stores: AssignedStore[];
}

export default function MonitorPerformancePage() {
  const { monitorId } = useParams<{ monitorId: string }>();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function fetchPerformance() {
      try {
        const res = await api.get(`/admin/dashboard/monitors/${monitorId}/performance`);
        if (!cancelled) {
          const raw = res.data;
          // API may return monitor fields at top level or nested under .monitor
          const normalized: PerformanceData = raw.monitor
            ? {
                ...raw,
                recent_shifts: raw.recent_shifts || [],
                assigned_stores: raw.assigned_stores || [],
              }
            : {
                monitor: {
                  id: raw.id,
                  full_name: raw.full_name,
                  email: raw.email,
                  role: raw.role,
                  current_shift: raw.current_shift,
                  shift_started_at: raw.shift_started_at,
                },
                today: raw.today || { shifts: 0, alerts: 0, activities: 0, hours: 0 },
                this_week: raw.this_week || { shifts: 0, alerts: 0, activities: 0, hours: 0 },
                last_30_days: raw.last_30_days || { shifts: 0, alerts: 0, activities: 0, hours: 0 },
                recent_shifts: raw.recent_shifts || [],
                assigned_stores: raw.assigned_stores || [],
              };
          setData(normalized);
          setLoading(false);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(extractErrorMessage(err, 'Failed to load performance data.'));
          setLoading(false);
        }
      }
    }
    fetchPerformance();
    return () => { cancelled = true; };
  }, [monitorId]);

  function formatDuration(minutes: number | null): string {
    if (minutes == null) return '—';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  function formatShiftDuration(startedAt: string | null): string {
    if (!startedAt) return '';
    const start = new Date(startedAt).getTime();
    const now = Date.now();
    const mins = Math.floor((now - start) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  if (!isAdmin) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <div style={styles.accessDenied}>
            <span style={{ fontSize: 36, color: 'var(--status-critical)', display: 'block', marginBottom: 12 }}>&#9888;</span>
            <h2 style={{ margin: '0 0 8px', fontSize: 'var(--text-xl)', fontFamily: 'var(--font-display)', color: 'var(--status-critical)' }}>Access Denied</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' }}>Lead or Admin role required.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate('/admin')} style={styles.backBtn}>
          &#8249; Back to Admin
        </button>

        {loading && <p style={styles.statusText}>Loading performance data...</p>}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {!loading && !error && data && (
          <>
            {/* Monitor header card */}
            <div style={styles.profileCard}>
              <div style={styles.profileTop}>
                <div>
                  <h2 style={styles.profileName}>{data.monitor.full_name}</h2>
                  <div style={styles.profileEmail}>{data.monitor.email}</div>
                </div>
                <div style={styles.profileRight}>
                  <span style={roleBadgeStyle(data.monitor.role)}>{data.monitor.role}</span>
                  <div style={styles.shiftStatus}>
                    <span style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: data.monitor.current_shift ? 'var(--status-success)' : 'var(--text-tertiary)',
                      boxShadow: data.monitor.current_shift ? '0 0 6px rgba(23,178,106,0.5)' : 'none',
                      flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', color: data.monitor.current_shift ? 'var(--status-success)' : 'var(--text-secondary)' }}>
                      {data.monitor.current_shift
                        ? `On Shift${data.monitor.shift_started_at ? ` (${formatShiftDuration(data.monitor.shift_started_at)})` : ''}`
                        : 'Off Shift'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats grid: Today / This Week / Last 30 Days */}
            <div style={styles.statsSection}>
              {([
                { label: 'Today', stats: data.today },
                { label: 'This Week', stats: data.this_week },
                { label: 'Last 30 Days', stats: data.last_30_days },
              ] as { label: string; stats: PeriodStats }[]).map((period) => (
                <div key={period.label} style={styles.statsPeriod}>
                  <div style={styles.periodLabel}>{period.label}</div>
                  <div style={styles.periodGrid}>
                    <div style={styles.periodStat}>
                      <div style={styles.periodValue}>{period.stats.shifts}</div>
                      <div style={styles.periodKey}>Shifts</div>
                    </div>
                    <div style={styles.periodStat}>
                      <div style={styles.periodValue}>{period.stats.alerts}</div>
                      <div style={styles.periodKey}>Alerts</div>
                    </div>
                    <div style={styles.periodStat}>
                      <div style={styles.periodValue}>{period.stats.activities}</div>
                      <div style={styles.periodKey}>Activities</div>
                    </div>
                    <div style={styles.periodStat}>
                      <div style={styles.periodValue}>{typeof period.stats.hours === 'number' ? period.stats.hours.toFixed(1) : period.stats.hours}</div>
                      <div style={styles.periodKey}>Hours</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Shifts */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Recent Shifts</h3>
              {data.recent_shifts.length === 0 && <p style={styles.statusText}>No recent shifts.</p>}
              {data.recent_shifts.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Time In</th>
                        <th style={styles.th}>Time Out</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Duration</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Alerts</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Activities</th>
                        <th style={styles.th}>Handoff Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_shifts.map((shift) => (
                        <tr key={shift.id}>
                          <td style={styles.td}>{new Date(shift.clock_in).toLocaleDateString()}</td>
                          <td style={styles.td}>{new Date(shift.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                          <td style={styles.td}>
                            {shift.clock_out
                              ? new Date(shift.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : <span style={{ color: 'var(--status-success)' }}>Active</span>
                            }
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{formatDuration(shift.duration_minutes)}</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{shift.alerts_count}</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{shift.activities_count}</td>
                          <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {shift.handoff_notes || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Assigned Stores */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Assigned Stores ({data.assigned_stores.length})</h3>
              {data.assigned_stores.length === 0 && <p style={styles.statusText}>No stores assigned.</p>}
              {data.assigned_stores.map((store) => (
                <div key={store.id} style={styles.storeItem}>
                  <div style={styles.storeItemName}>{store.name}</div>
                  <div style={styles.storeItemAddr}>{store.address}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function roleBadgeStyle(role: string): React.CSSProperties {
  const map: Record<string, { bg: string; color: string }> = {
    admin: { bg: 'var(--accent-muted)', color: 'var(--accent-text)' },
    lead: { bg: 'var(--status-warning-muted)', color: 'var(--status-warning)' },
    monitor: { bg: 'var(--status-neutral-muted)', color: 'var(--text-secondary)' },
  };
  const colors = map[role] || map.monitor;
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    background: colors.bg,
    color: colors.color,
    textTransform: 'capitalize',
  };
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
  },
  main: {
    maxWidth: 900,
    margin: '0 auto',
    padding: 'var(--space-8) var(--space-6)',
  },
  backBtn: {
    display: 'inline-block',
    background: 'none',
    border: 'none',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    padding: 0,
    marginBottom: 'var(--space-5)',
  },
  statusText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)',
    marginBottom: 'var(--space-4)',
  },
  accessDenied: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid rgba(255,71,87,0.3)',
    padding: '40px 32px',
    textAlign: 'center',
  },

  // Profile card
  profileCard: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
    padding: '22px 24px',
    marginBottom: 'var(--space-6)',
  },
  profileTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  profileName: {
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: '0 0 4px',
  },
  profileEmail: {
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
  },
  profileRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
  },
  shiftStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  // Stats section
  statsSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-6)',
  },
  statsPeriod: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
    borderLeft: '3px solid var(--accent)',
    padding: '18px 20px',
  },
  periodLabel: {
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 14,
  },
  periodGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
  },
  periodStat: {
    textAlign: 'center',
  },
  periodValue: {
    fontSize: 'var(--text-xl)',
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
    lineHeight: 1,
    marginBottom: 4,
  },
  periodKey: {
    fontSize: 'var(--text-xs)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
  },

  // Sections
  section: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
    padding: '20px 24px',
    marginBottom: 'var(--space-5)',
  },
  sectionTitle: {
    fontSize: 'var(--text-lg)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: '0 0 14px',
  },

  // Table
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'var(--font-body)',
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    background: 'var(--table-header-bg)',
    borderBottom: '1px solid var(--border-default)',
  },
  td: {
    padding: '10px 12px',
    fontSize: 'var(--text-base)',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-subtle)',
  },

  // Store items
  storeItem: {
    padding: '12px 0',
    borderBottom: '1px solid var(--border-subtle)',
  },
  storeItemName: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    marginBottom: 2,
  },
  storeItemAddr: {
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
  },
};
